import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';

/**
 * Type-and-search autocomplete for product selection on line items.
 *
 * Props:
 *   value        – currently selected product_id (string)
 *   onChange     – (newProductId: string|'') => void  -- called when user picks/clears
 *   products     – array of product records [{ id, name, ... }]
 *   placeholder  – placeholder text for the input
 *   testId       – data-testid attribute
 *   disabled     – disables the control
 */
export const ProductAutocomplete = ({
  value,
  onChange,
  products = [],
  placeholder = 'Type to search...',
  testId,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  const selected = products.find(p => p.id === value);

  // Format an entry as the display label (name + optional phone in parens)
  const formatLabel = (p) => {
    if (!p) return '';
    const ph = p.phone || p.mobile || p.contact_number || '';
    return ph ? `${p.name || ''} (${ph})` : (p.name || '');
  };

  // Sync the input text with the selected product when value changes externally
  useEffect(() => {
    if (selected) {
      setQuery(formatLabel(selected));
    } else {
      setQuery('');
    }
  }, [value, selected]);

  const normalize = (str) => (str || '').toLowerCase().replace(/[\s\-\/\\.,:;_()+]+/g, '');
  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const filtered = (() => {
    const terms = query.trim().split(/\s+/).filter(Boolean);
    if (!terms.length) return products.slice(0, 50);
    const pattern = new RegExp(terms.map(t => `(?=.*${escapeRegex(normalize(t))})`).join(''), 'i');
    return products.filter(p => {
      const haystack = [
        p.name, p.product_code, p.sku,
        p.phone, p.mobile, p.contact_number,
      ].map(normalize).join('');
      return pattern.test(haystack);
    }).slice(0, 50);
  })();

  const handleSelect = (p) => {
    onChange(p.id);
    setQuery(formatLabel(p));
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            // If they clear and start typing fresh, clear the selection
            if (selected && e.target.value !== formatLabel(selected)) {
              onChange('');
            }
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          data-testid={testId}
          className="w-full pl-2 pr-7 py-1.5 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none disabled:bg-slate-50 disabled:cursor-not-allowed"
        />
        {selected && !disabled ? (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleClear}
            tabIndex={-1}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-red-500"
            title="Clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : !selected ? (
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
        ) : null}
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-30 max-h-60 overflow-y-auto">
          {filtered.map(p => {
            const phone = p.phone || p.mobile || p.contact_number || '';
            return (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(p)}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-orange-50 border-b border-slate-100 last:border-b-0 ${
                p.id === value ? 'bg-orange-50 font-medium' : ''
              }`}
            >
              <span className="text-slate-800">{p.name}</span>
              {phone && <span className="text-slate-500 text-xs ml-2">· {phone}</span>}
              {p.sku && <span className="text-slate-400 text-xs ml-2">({p.sku})</span>}
            </button>
            );
          })}
        </div>
      )}

      {open && filtered.length === 0 && query.trim() && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-30 px-3 py-2 text-sm text-slate-400">
          No products match &ldquo;{query}&rdquo;
        </div>
      )}
    </div>
  );
};
